import { autoUpdate, flip, FloatingPortal, hide, offset, shift, size, useFloating } from '@floating-ui/react';
import { useEffect } from 'react';

import type { HistoryItem, InteractionHistoryActions } from '@poseidon/components/ContactDetail';
import { InteractionHistoryItem } from '@poseidon/components/ContactDetail';
import { ESC_PRIORITY, useEscStack } from '@poseidon/components/contexts';
import { Button } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

interface Props {
  entry: HistoryItem;
  actions: InteractionHistoryActions;
  /** The card it opened from — the popover tracks it as the canvas pans / zooms. */
  anchor: Element | null;
  onClose: () => void;
}

/** The tallest the entry may grow before it scrolls inside itself (screen px). */
const MAX_HEIGHT_PX = 520;

/**
 * One history entry opened from its card on the sequence canvas: the FULL entry — the whole message, the
 * transcript, Respond — at natural size. Portaled out of the canvas, so it's never scaled by the zoom,
 * clipped by the viewport, or painted under a node; and it scrolls INSIDE itself (overscroll contained), so a
 * long reply scrolls the reply — never the canvas behind it. Escape (shared esc stack, above the contact
 * panel's own handler) or a click elsewhere closes it.
 */
export function SequenceHistoryPopover({ entry, actions, anchor, onClose }: Props) {
  const float = useFloating({
    open: true,
    // Below the card (above it near the bottom): a narrow panel has no room BESIDE it.
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: (reference, floating, update) =>
      autoUpdate(reference, floating, update, { animationFrame: true }),
    middleware: [
      offset(8),
      flip({ padding: 12 }),
      shift({ padding: 12, crossAxis: true }),
      // Never taller than the room on screen — past that the entry scrolls inside the popover.
      size({
        padding: 12,
        apply({ availableHeight, elements }) {
          elements.floating.style.maxHeight = `${Math.max(160, Math.min(MAX_HEIGHT_PX, availableHeight))}px`;
        },
      }),
      // Panned so its card left the canvas → hide with it, rather than pinning to the screen edge.
      hide({ strategy: 'referenceHidden' }),
    ],
    elements: { reference: anchor },
  });

  useEscStack('SEQUENCE_HISTORY_POPOVER', ESC_PRIORITY.INTERSTITIAL, onClose);
  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!(e.target instanceof Element)) return;
      // Inside the popover, on another card (it opens that one instead), or in a menu / tooltip the entry
      // floats out of itself — all part of using it, not a click away.
      if (
        e.target.closest(
          '[data-history-popover], [data-history-card], [data-floating-ui-portal], .tippy-box, [popover]',
        ) != null
      )
        return;
      onClose();
    };
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [onClose]);

  return (
    <FloatingPortal>
      <div
        ref={float.refs.setFloating}
        data-history-popover
        style={{
          ...float.floatingStyles,
          visibility: float.middlewareData.hide?.referenceHidden === true ? 'hidden' : 'visible',
        }}
        className="z-50 flex w-[360px] max-w-[calc(100vw-24px)] flex-col overflow-y-auto overscroll-contain rounded-xl border border-lynch-200 bg-white p-3 shadow-xl dark:border-slate-700 dark:bg-slate-900">
        <div className="-mt-1 mb-1 flex justify-end">
          <Button size="2xs" text icon={<General.XMark />} aria-label="Close" tooltip="Close" onClick={onClose} />
        </div>
        <InteractionHistoryItem entry={entry} actions={actions} isLast />
      </div>
    </FloatingPortal>
  );
}
